import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FaSave, FaTimes, FaEdit, FaTrash, FaSearch, FaPlus, FaChevronDown, FaChevronUp, FaCheckSquare, FaRegSquare } from 'react-icons/fa';
import Swal from 'sweetalert2';
import api from '../../../utils/api';
import useColleges from '../../../utils/useColleges';
import { useAuth } from '../../../context/AuthContext';
import './AddBank.css';

const AddBank = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'superadmin';
  const isAdminOnly = isSuperAdmin || user?.role === 'admin';
  const { colleges, loading: collegesLoading } = useColleges();
  
  // State for Lists
  const [banks, setBanks] = useState([]);
  
  // UI States
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  
  // Search & Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCollege, setFilterCollege] = useState('');
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  
  // Form State
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);

  // Multi-Select College Dropdown State
  const [showCollegeDropdown, setShowCollegeDropdown] = useState(false);
  const [collegeSearchQuery, setCollegeSearchQuery] = useState('');
  
  // Bank Name Dropdown State
  const [showBankNameDropdown, setShowBankNameDropdown] = useState(false);
  const [bankNameSearchQuery, setBankNameSearchQuery] = useState('');

  const searchRef = useRef(null);
  const collegeDropdownRef = useRef(null);
  const bankNameDropdownRef = useRef(null);

  const [formData, setFormData] = useState({
    colleges: [], 
    name: '',
    accountNumber: ''
  });

  // Auto-set college for clerk/collegeadmin
  useEffect(() => {
    if (!isAdminOnly && colleges.length > 0) {
      const cid = String(colleges[0]._id);
      setFilterCollege(cid);
      setFormData(prev => ({ ...prev, colleges: [cid] }));
    }
  }, [isAdminOnly, colleges]);

  // Get unique bank names from existing banks
  const uniqueBankNames = useMemo(() => {
    const names = [...new Set(banks.map(b => b.name).filter(Boolean))];
    return names.sort();
  }, [banks]);

  // Fetch initial data
  useEffect(() => {
    fetchBanks();
  }, []);

  const fetchBanks = async () => {
    setFetchLoading(true);
    try {
      const { data } = await api.get('/banks');
      setBanks(data.data || []);
    } catch (error) {
      console.error('Error fetching banks:', error);
    } finally {
      setFetchLoading(false);
    }
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowSearchDropdown(false);
      if (collegeDropdownRef.current && !collegeDropdownRef.current.contains(e.target)) setShowCollegeDropdown(false);
      if (bankNameDropdownRef.current && !bankNameDropdownRef.current.contains(e.target)) setShowBankNameDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Handle Input Changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'name') {
      setFormData(prev => ({ ...prev, [name]: value.toUpperCase() }));
      setBankNameSearchQuery(value.toUpperCase());
      setShowBankNameDropdown(true);
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Multi-select Toggle Logic
  const handleCollegeToggle = (collegeId) => {
    setFormData(prev => {
      const isSelected = prev.colleges.includes(collegeId);
      if (isSelected) {
        return { ...prev, colleges: prev.colleges.filter(id => id !== collegeId) };
      } else {
        return { ...prev, colleges: [...prev.colleges, collegeId] };
      }
    });
  };

  const handleSelectAllColleges = () => {
    const allIds = colleges.map(c => c._id);
    setFormData(prev => ({ ...prev, colleges: allIds }));
  };

  const handleClearAllColleges = () => {
    setFormData(prev => ({ ...prev, colleges: [] }));
  };

  // Reset Form
  const resetForm = () => {
    setFormData({
      colleges: isAdminOnly ? [] : (colleges.length > 0 ? [colleges[0]._id] : []),
      name: '',
      accountNumber: ''
    });
    setEditingId(null);
    setShowAddForm(false);
    setShowCollegeDropdown(false);
    setCollegeSearchQuery('');
    setShowBankNameDropdown(false);
    setBankNameSearchQuery('');
  };

  // Edit Bank Setup
  const handleEdit = (bank) => {
    setEditingId(bank._id);
    setFormData({
      colleges: Array.isArray(bank.colleges) 
        ? bank.colleges.map(c => c._id || c) 
        : (bank.college ? [bank.college._id || bank.college] : []),
      name: bank.name || '',
      accountNumber: bank.accountNumber || ''
    });
    setBankNameSearchQuery(bank.name || '');
    setShowAddForm(true);
    setExpandedRow(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Delete Bank
  const handleDelete = async (bankId, collegeId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "This will remove the bank from this college!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e74c3c',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/banks/${bankId}?college=${collegeId}`);
        Swal.fire('Deleted!', 'The bank has been removed from this college.', 'success');
        fetchBanks();
      } catch (error) {
        Swal.fire('Error!', error.response?.data?.message || 'Failed to delete bank.', 'error');
      }
    }
  };

  // Submit Add/Update
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.colleges.length === 0) {
      return Swal.fire('Validation Error', 'Please select at least one college.', 'warning');
    }

    if (!formData.name.trim() || !formData.accountNumber.trim()) {
      return Swal.fire('Validation Error', 'Bank Name and Account Number are required.', 'warning');
    }

    setLoading(true);
    try {
      const payload = { 
        colleges: formData.colleges,
        name: formData.name.trim().toUpperCase(),
        accountNumber: formData.accountNumber.trim()
      };

      if (editingId) {
        await api.put(`/banks/${editingId}`, payload);
        Swal.fire('Updated!', 'Bank updated successfully.', 'success');
      } else {
        await api.post('/banks', payload);
        Swal.fire('Added!', 'Bank added successfully.', 'success');
      }
      
      resetForm();
      fetchBanks();
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Something went wrong.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ── Filters & Grouping ──────────────────────────────────────────
  const displayedBanks = useMemo(() => {
    return banks.filter(bank => {
      const search = searchQuery.toLowerCase();
      const nameMatch = (bank.name || '').toLowerCase().includes(search);
      const accMatch = (bank.accountNumber || '').toLowerCase().includes(search);
      const matchSearch = nameMatch || accMatch;
      
      let matchCollege = true;
      if (filterCollege) {
        const bankColleges = Array.isArray(bank.colleges) 
          ? bank.colleges.map(c => c._id || c) 
          : (bank.college ? [bank.college._id || bank.college] : []);
        matchCollege = bankColleges.includes(filterCollege);
      }
      
      return matchSearch && matchCollege;
    });
  }, [banks, searchQuery, filterCollege]);

  const collegesToDisplay = useMemo(() => {
    if (filterCollege) return colleges.filter(c => c._id === filterCollege);
    return colleges.length > 0 ? colleges : [{ _id: 'default', name: 'Loading Colleges...' }];
  }, [colleges, filterCollege]);

  const filteredDropdownColleges = colleges.filter(c => 
    c.name.toLowerCase().includes(collegeSearchQuery.toLowerCase())
  );
  
  const filteredBankNames = uniqueBankNames.filter(name => 
    name.toLowerCase().includes(bankNameSearchQuery.toLowerCase())
  );

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <div className="add-bank-with-account-container">
      <h2 className="add-bank-with-account-title">Bank Management</h2>

      {/* ── Top Controls ── */}
      <div className="add-bank-with-account-controls">
        <div className="add-bank-with-account-search-wrapper" ref={searchRef}>
          <FaSearch className="add-bank-with-account-search-icon" />
          <input
            type="text"
            className="add-bank-with-account-search-input"
            placeholder="Search bank name, acc no..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setShowSearchDropdown(true); }}
            onFocus={() => setShowSearchDropdown(true)}
          />
          {showSearchDropdown && searchQuery && (
            <div className="add-bank-with-account-search-dropdown">
              <ul>
                {displayedBanks.length > 0 ? displayedBanks.slice(0, 5).map(b => (
                  <li key={b._id} onClick={() => { setSearchQuery(b.name); setShowSearchDropdown(false); }}>
                    {b.name} <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>({b.accountNumber})</span>
                  </li>
                )) : (
                  <div className="add-bank-with-account-search-empty">No banks found</div>
                )}
              </ul>
            </div>
          )}
        </div>

        {isAdminOnly && (
          <select className="add-bank-with-account-filter-select" value={filterCollege} onChange={(e) => setFilterCollege(e.target.value)}>
            <option value="">All Colleges</option>
            {colleges.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
        )}

        <button className="add-bank-with-account-btn add-bank-with-account-btn-add" onClick={() => { if (showAddForm) resetForm(); else setShowAddForm(true); }}>
          <FaPlus /> {showAddForm ? 'Close Form' : 'Add Bank'}
        </button>
      </div>

      {/* ── Add / Edit Inline Form ── */}
      {showAddForm && (
        <div className="add-bank-with-account-inline-form">
          <h3 className="add-bank-with-account-form-title">{editingId ? '✏️ Edit Bank Details' : '📝 Add New Bank'}</h3>
          
          <form onSubmit={handleSubmit}>
            <div className="add-bank-with-account-form-grid">
              
              {isAdminOnly && (
                <div className="add-bank-with-account-form-group add-bank-with-account-col-span-2" ref={collegeDropdownRef}>
                  <label>Assign to Colleges <span className="add-bank-with-account-req">*</span></label>
                  <div className="add-bank-with-account-multi-select">
                    <div 
                      className="add-bank-with-account-multi-select-header" 
                      onClick={() => setShowCollegeDropdown(!showCollegeDropdown)}
                    >
                      <span>
                        {formData.colleges.length === 0 
                          ? '-- Select Colleges --' 
                          : `${formData.colleges.length} College(s) Selected`}
                      </span>
                      {showCollegeDropdown ? <FaChevronUp /> : <FaChevronDown />}
                    </div>

                    {showCollegeDropdown && (
                      <div className="add-bank-with-account-multi-select-body">
                        <div className="add-bank-with-account-multi-select-search">
                          <FaSearch />
                          <input 
                            type="text" 
                            placeholder="Search college..." 
                            value={collegeSearchQuery}
                            onChange={(e) => setCollegeSearchQuery(e.target.value)}
                          />
                        </div>
                        <div className="add-bank-with-account-multi-select-actions">
                          <button type="button" onClick={handleSelectAllColleges}>Select All</button>
                          <button type="button" onClick={handleClearAllColleges}>Clear</button>
                        </div>
                        <div className="add-bank-with-account-multi-select-list">
                          {filteredDropdownColleges.length > 0 ? (
                            filteredDropdownColleges.map(c => (
                              <div 
                                key={c._id} 
                                className={`add-bank-with-account-multi-select-item ${formData.colleges.includes(c._id) ? 'selected' : ''}`}
                                onClick={() => handleCollegeToggle(c._id)}
                              >
                                {formData.colleges.includes(c._id) ? <FaCheckSquare className="add-bank-with-account-check-icon" /> : <FaRegSquare className="add-bank-with-account-check-icon" />}
                                <span>{c.name}</span>
                              </div>
                            ))
                          ) : (
                            <div className="add-bank-with-account-multi-select-empty">No colleges found.</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="add-bank-with-account-form-group" ref={bankNameDropdownRef}>
                <label>Bank Name <span className="add-bank-with-account-req">*</span></label>
                <div style={{ position: 'relative' }}>
                  <input 
                    type="text" 
                    name="name" 
                    placeholder="e.g. STATE BANK OF INDIA" 
                    value={formData.name} 
                    onChange={handleInputChange}
                    onFocus={() => setShowBankNameDropdown(true)}
                    required 
                    style={{ textTransform: 'uppercase' }}
                    autoComplete="off"
                  />
                  {showBankNameDropdown && filteredBankNames.length > 0 && (
                    <div className="add-bank-with-account-bank-name-dropdown">
                      {filteredBankNames.map((bankName, idx) => (
                        <div 
                          key={idx} 
                          className="add-bank-with-account-bank-name-item"
                          onClick={() => {
                            setFormData(prev => ({ ...prev, name: bankName }));
                            setBankNameSearchQuery(bankName);
                            setShowBankNameDropdown(false);
                          }}
                        >
                          {bankName}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="add-bank-with-account-form-group">
                <label>Account Number <span className="add-bank-with-account-req">*</span></label>
                <input type="text" name="accountNumber" className="add-bank-with-account-input-acc" placeholder="e.g. 1234567890" value={formData.accountNumber} onChange={handleInputChange} required />
              </div>

            </div>

            <div className="add-bank-with-account-form-buttons">
              <button type="button" className="add-bank-with-account-btn-cancel" onClick={resetForm} disabled={loading}>
                Cancel
              </button>
              <button type="submit" className="add-bank-with-account-btn-confirm" disabled={loading}>
                {loading ? 'Saving...' : editingId ? 'Update Bank' : 'Save Bank'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── College-wise Grouped Tables ── */}
      <div className="add-bank-with-account-grouped-container">
        {fetchLoading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Loading banks...</div>
        ) : (
          collegesToDisplay.map(college => {
            const collegeBanks = displayedBanks.filter(bank => {
              const bankColleges = Array.isArray(bank.colleges) 
                ? bank.colleges.map(c => c._id || c) 
                : (bank.college ? [bank.college._id || bank.college] : []);
              return bankColleges.includes(college._id);
            });

            if (collegeBanks.length === 0 && !filterCollege && isAdminOnly) return null;

            return (
              <div key={college._id} className="add-bank-with-account-table-group">
                <h3 className="add-bank-with-account-group-header">🏫 {college.name}</h3>
                <div className="add-bank-with-account-table-wrapper">
                  <table className="add-bank-with-account-table">
                    <thead>
                      <tr>
                        <th width="50px">S.No</th>
                        <th className="add-bank-with-account-align-left">Bank Name</th>
                        <th className="add-bank-with-account-col-acc">Account Number</th>
                        <th className="add-bank-with-account-col-created">Created At</th>
                        <th className="add-bank-with-account-col-updated">Updated At</th>
                        <th className="add-bank-with-account-col-action" width="100px">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {collegeBanks.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="add-bank-with-account-empty-td">
                            No banks recorded for this college.
                          </td>
                        </tr>
                      ) : (
                        collegeBanks.map((bank, index) => (
                          <React.Fragment key={bank._id}>
                            <tr className="add-bank-with-account-main-row">
                              <td>{index + 1}</td>
                              
                              <td className="add-bank-with-account-align-left add-bank-with-account-td-name">
                                <div className="add-bank-with-account-name-wrapper" onClick={() => setExpandedRow(expandedRow === bank._id ? null : bank._id)}>
                                  <div style={{ fontWeight: 700, color: '#1e293b' }}>{bank.name}</div>
                                  <button className="add-bank-with-account-expand-btn">
                                    {expandedRow === bank._id ? <FaChevronUp /> : <FaChevronDown />}
                                  </button>
                                </div>
                              </td>
                              
                              <td className="add-bank-with-account-col-acc">
                                <span className="add-bank-with-account-acc-badge">{bank.accountNumber}</span>
                              </td>

                              <td className="add-bank-with-account-col-created" style={{ color: '#64748b', fontSize: '0.85rem' }}>
                                {fmtDate(bank.createdAt)}
                              </td>

                              <td className="add-bank-with-account-col-updated" style={{ color: '#64748b', fontSize: '0.85rem' }}>
                                {fmtDate(bank.updatedAt)}
                              </td>
                              
                              <td className="add-bank-with-account-col-action">
                                <div className="add-bank-with-account-action-btns">
                                  <button className="add-bank-with-account-btn-edit" onClick={() => handleEdit(bank)} title="Edit"><FaEdit /></button>
                                  <button className="add-bank-with-account-btn-delete" onClick={() => handleDelete(bank._id, college._id)} title="Delete"><FaTrash /></button>
                                </div>
                              </td>
                            </tr>
                            
                            {/* Expanded Row for Hidden Metadata */}
                            {expandedRow === bank._id && (
                              <tr className="add-bank-with-account-expanded-row">
                                <td colSpan="6">
                                  <div className="add-bank-with-account-expanded-content">
                                    <div className="add-bank-with-account-exp-grid">
                                      
                                      {/* Account Number (Shows < 480px) */}
                                      <div className="add-bank-with-account-exp-item add-bank-with-account-exp-acc">
                                        <span className="add-bank-with-account-exp-label">Account Number</span>
                                        <span className="add-bank-with-account-exp-value">
                                          <span className="add-bank-with-account-acc-badge">{bank.accountNumber}</span>
                                        </span>
                                      </div>

                                      {/* Created At (Shows < 991px) */}
                                      <div className="add-bank-with-account-exp-item add-bank-with-account-exp-created">
                                        <span className="add-bank-with-account-exp-label">Created At</span>
                                        <span className="add-bank-with-account-exp-value">{fmtDate(bank.createdAt)} {fmtTime(bank.createdAt)}</span>
                                      </div>

                                      {/* Updated At (Shows < 991px) */}
                                      <div className="add-bank-with-account-exp-item add-bank-with-account-exp-updated">
                                        <span className="add-bank-with-account-exp-label">Updated At</span>
                                        <span className="add-bank-with-account-exp-value">{fmtDate(bank.updatedAt)} {fmtTime(bank.updatedAt)}</span>
                                      </div>

                                      {/* Action Buttons (Shows < 768px) */}
                                      <div className="add-bank-with-account-exp-item add-bank-with-account-exp-action">
                                        <span className="add-bank-with-account-exp-label">Actions</span>
                                        <span className="add-bank-with-account-exp-value">
                                          <div className="add-bank-with-account-action-btns" style={{ justifyContent: 'flex-start' }}>
                                            <button className="add-bank-with-account-btn-edit" onClick={() => handleEdit(bank)} title="Edit"><FaEdit /> Edit</button>
                                            <button className="add-bank-with-account-btn-delete" onClick={() => handleDelete(bank._id, college._id)} title="Delete"><FaTrash /> Delete</button>
                                          </div>
                                        </span>
                                      </div>

                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })
        )}
        
        {!fetchLoading && displayedBanks.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#64748b', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            No banks found matching your criteria.
          </div>
        )}
      </div>
    </div>
  );
};

export default AddBank;